<template>
    <v-row class="mx-1">
        <v-col class="text-h6 d-flex align-end" cols="12" sm="6">
            <v-tooltip bottom>
                <template v-slot:activator="{on}">
                    <v-btn :loading="exportingData"
                           class="mr-2"
                           color="kbPurple"
                           icon
                           outlined
                           v-on="on"
                           @click="exportAttendees()">
                        <v-icon>
                            cloud_download
                        </v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('general.exportAssistants') }}</span>
            </v-tooltip>
            {{ $t('general.assistantsList') }}
        </v-col>

        <v-col cols="12" sm="6">
            <v-text-field
                color="kbDarkBlueText"
                v-model="search"
                append-icon="search"
                :placeholder="$t('general.search')"
                single-line
                hide-details
                clearable>
                <template slot="append-outer">
                    <v-menu :close-on-content-click="false" offset-y v-model="showOptions">
                        <template v-slot:activator="{ on }">
                            <v-icon color="kbDarkBlueText" v-on="on">filter_list</v-icon>
                        </template>

                        <v-card>
                            <v-card>
                                <v-card-text>
                                    <v-row>
                                        <v-col cols="12">
                                            <v-text-field clearable
                                                          color="kbDarkBlueText"
                                                          :label="$t('general.assistantId')"
                                                          v-model="advancedSearch._id">
                                            </v-text-field>
                                        </v-col>

                                        <v-col cols="12">
                                            <v-text-field clearable
                                                          color="kbDarkBlueText"
                                                          :label="$t('general.document')"
                                                          v-model="advancedSearch.document">
                                            </v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-card>
                    </v-menu>

                    <v-icon color="kbDarkBlueText" class="ml-2" @click="getAttendees">refresh</v-icon>
                </template>
            </v-text-field>
        </v-col>

        <v-col cols="12">
            <ViewAttendee v-model="viewAttendeeDialog" :attendee="selectedAttendee"></ViewAttendee>

            <v-data-table :headers="headers"
                          :items="attendees"
                          class="elevation-2"
                          :loading="loadingAttendees"
                          :options.sync="options"
                          :footer-props="{
                                            'items-per-page-text': `${$t('general.assistants')} ${$t('general.perPage')}`,
                                            'items-per-page-options': [50, 100, 200]
                                          }"
                          :server-items-length="totalAttendees"
                          :must-sort="false">
                <template v-slot:item="{ item }">
                    <tr>
                        <td class="text-capitalize">{{ item.name || $t('general.noName') }}</td>
                        <td>{{ item.email || item.phone.join(', ') }}</td>
                        <td>{{ item.ticket.title }}</td>
                        <td>{{ item.manualCreation ? $t('general.manual') : $t('general.automatic') }}</td>
                        <td>{{ convertTZToUserTZ(item.createdAt) | moment('DD/MM/YYYY hh:mma') }}</td>
                        <td class="px-0 mx-0">
                            <div class="data-table-actions d-flex justify-center align-center">
                                <v-btn icon @click="showUser(item)" color="kbDarkBlueText">
                                    <v-icon>remove_red_eye</v-icon>
                                </v-btn>

                                <a style="text-decoration: none" v-if="item.urlDownload.url" :href="item.urlDownload.url" target="_blank">
                                    <v-btn icon color="kbDarkBlueText" text>
                                        <v-icon>fas fa-qrcode</v-icon>
                                    </v-btn>
                                </a>
                            </div>
                        </td>
                    </tr>
                </template>

                <template v-slot:no-data>
                    <v-row>
                        <v-col class="text-center" cols="12" v-if="loadingAttendees">
                            {{ $t('general.loadingAssistants') }}
                        </v-col>
                        <v-col cols="12" class="red--text text-center" v-else>
                            {{ $t('general.noAssistantsRegistered') }}
                        </v-col>
                    </v-row>
                </template>
            </v-data-table>
        </v-col>
    </v-row>
</template>

<script>
import { mapState } from 'vuex'
import eventsService from '@/services/eventsService.js'
import notifications from '@/libs/notifications'
import TimeZones from '@/mixins/TimeZones'
import ViewAttendee from '@/pages/App/Attendees/dialogs/ViewAttendee.vue'
export default {
  components: { ViewAttendee },
  computed: {
    ...mapState({
      advancedSearch: state => state.attendees.advancedSearch,
      loadingAttendees: state => state.attendees.loadingAttendees,
      paginationAttendees: state => state.attendees.paginationAttendees,
      selectedApp: state => state.apps.selectedApp,
      totalAttendees: state => state.attendees.totalAttendees,
      attendees: state => state.attendees.attendees,
      events: state => state.events.events
    }),
    search: {
      get () {
        return this.$store.state.attendees.search
      },
      set (value) {
        this.$store.commit('SET_SEARCH_ATTENDEES', value)
      }
    },
    headers () {
      return [
        { text: this.$t('general.name'), value: 'name' },
        { text: this.$t('general.emailOrPhone'), value: 'email' },
        { text: 'Ticket', value: 'ticket' },
        { text: this.$t('general.creationType'), value: 'manualCreation' },
        { text: this.$t('general.registerDate'), value: 'createdAt' },
        { text: this.$t('general.actions'), align: 'center', sortable: false, value: '' }
      ]
    }
  },
  data () {
    return {
      exportingData: false,
      options: {},
      selectedAttendee: null,
      showOptions: false,
      viewAttendeeDialog: false
    }
  },
  methods: {
    async exportAttendees () {
      try {
        this.exportingData = true

        await eventsService.exportData(
          this.$store.getters.token,
          this.$store.getters.companyUuid,
          this.$store.getters.appUuid,
          this.$route.params.eventid,
          this.$t('general.assistants')
        )

        this.exportingData = false
        notifications.success(this.$t('general.assistantsExportedSuccessfully'))
      } catch (error) {
        this.exportingData = false
        notifications.error(error.message)
      }
    },
    getAttendees () {
      this.advancedSearch.eventId = this.$route.params.eventid
      this.$store.commit('SET_PAGINATION_ATTENDEES', { page: this.options.page, itemsPerPage: this.options.itemsPerPage, sortBy: this.options.sortBy[0], descending: this.options.sortDesc[0] })
      this.$store.dispatch('getAttendees')
    },
    showUser (user) {
      this.selectedAttendee = user
      this.viewAttendeeDialog = true
    }
  },
  mixins: [TimeZones],
  watch: {
    options: {
      advancedSearch: {
        deep: true,
        handler () {
          this.getAttendees()
        }
      },
      handler () {
        this.getAttendees()
        this.$vuetify.goTo(0)
      },
      deep: true
    },
    search () {
      this.options.page = 1
      this.getAttendees()
    }
  }
}
</script>
